import React from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
} from "formik"
import { Grid } from "@mui/material"

import i18n from "config/i18n"
import Input from "components/ui/Input"
import { Card, Headline, Submit } from "components/Sign/elements"
import { ClientMobileIdFormValues } from "types"
import {
  PERSONAL_CODE_LENGTH,
  PERSONAL_CODE_REGEX,
  PHONE_LENGTH,
  PHONE_REGEX,
} from "helpers/client"

interface FormProps {
  initialValues: {
    personal_code?: string
    phone?: string
  }
  onSubmit: (values: object) => void
}

type Props = FormikProps<ClientMobileIdFormValues> & FormProps

const MobileIdForm = ({ errors, isSubmitting }: Props) => {
  const { t } = useTranslation()

  return (
    <Form>
      <Card elevation={0}>
        <Headline>{t("providers.mobile_id.title")}</Headline>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Field name="personal_code">
              {({ field }: FieldProps) => (
                <Input
                  light
                  fullWidth
                  label={t("features.client.personal_code")}
                  helperText={errors.personal_code}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={6}>
            <Field name="phone">
              {({ field }: FieldProps) => (
                <Input
                  light
                  fullWidth
                  label={t("features.client.phone")}
                  helperText={errors.phone}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </Card>
      <Submit type="submit" disabled={isSubmitting}>
        {t("common.sign")}
      </Submit>
    </Form>
  )
}

const MobileIdFormWithFormik = withFormik<FormProps, ClientMobileIdFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props: { onSubmit } }) => onSubmit(values),
  validate: (values: ClientMobileIdFormValues) => {
    const errors: FormikErrors<ClientMobileIdFormValues> = {}
    const { personal_code, phone } = values

    if (!personal_code) {
      errors.personal_code = i18n.t("validation.field.required", {
        field: i18n.t("features.client.personal_code"),
      })
    } else if (
      !PERSONAL_CODE_REGEX.test(personal_code) ||
      personal_code.length != PERSONAL_CODE_LENGTH
    ) {
      errors.personal_code = i18n.t("validation.field.invalid", {
        field: i18n.t("features.client.personal_code"),
      })
    }

    if (!phone) {
      errors.phone = i18n.t("validation.field.required", {
        field: i18n.t("features.client.phone"),
      })
    } else if (!PHONE_REGEX.test(phone) || phone.length != PHONE_LENGTH) {
      errors.phone = i18n.t("validation.field.invalid", {
        field: i18n.t("features.client.phone"),
      })
    }

    return errors
  },
})(MobileIdForm)

export default MobileIdFormWithFormik
